import axios from 'axios';
import {
  startRegistration, startAuthentication, browserSupportsWebAuthn,
} from '@simplewebauthn/browser';

// Isolated client for the passkey service (passkey.app.lxop.in).
//
// Its own stack because the main API stack is at the CloudFormation resource
// limit. Sessions it issues are identical to the main stack's, so the rest of
// the app does not care which one signed the member in.
const BASE_URL =
  (typeof window !== 'undefined' && window.electronEnv?.passkeyApiUrl) ||
  process.env.REACT_APP_PASSKEY_API_URL ||
  'https://passkey.app.lxop.in';

const passkeyApi = axios.create({ baseURL: BASE_URL, timeout: 20000 });

passkeyApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('lxops_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// A passkey is bound to the web app's origin. The desktop build loads from a
// different one, so the ceremony can never succeed there — treat it as
// unsupported rather than letting members hit an opaque browser error.
const isElectron = () => typeof window !== 'undefined' && !!window.electronEnv;

export function passkeysSupported() {
  try {
    return !isElectron() && browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/** Register a new passkey for the signed-in member. */
export async function registerPasskey(label) {
  const { data } = await passkeyApi.post('/passkeys/register/begin', {});
  const attestation = await startRegistration({ optionsJSON: data.options });
  const { data: done } = await passkeyApi.post('/passkeys/register/finish', {
    response: attestation,
    label,
  });
  return done;
}

/** Sign in with a passkey. Takes no email — the authenticator picks the account. */
export async function loginWithPasskey() {
  const { data } = await passkeyApi.post('/passkey-login/begin', {});
  const assertion = await startAuthentication({ optionsJSON: data.options });
  const { data: done } = await passkeyApi.post('/passkey-login/finish', {
    response: assertion,
    session_id: data.session_id,
  });
  return done; // { token, user }
}

export async function listPasskeys() {
  const { data } = await passkeyApi.get('/passkeys');
  return data.passkeys || [];
}

export async function deletePasskey(credentialId) {
  const { data } = await passkeyApi.delete(`/passkeys/${encodeURIComponent(credentialId)}`);
  return data;
}

// The browser throws DOMExceptions with names, not useful messages. Map the
// ones a member can actually cause onto something they can act on.
export function passkeyErrorMessage(err, t) {
  const name = err?.name || '';
  if (name === 'NotAllowedError')    return t('passkey.errCancelled');
  if (name === 'InvalidStateError')  return t('passkey.errAlreadyRegistered');
  if (name === 'AbortError')         return t('passkey.errCancelled');
  if (name === 'SecurityError')      return t('passkey.errSecurity');
  return err?.response?.data?.error || err?.message || t('passkey.errGeneric');
}

export default passkeyApi;
